/**
 * @file useBoardArchive.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-11-03
 * @description "Archive order…" and "Archive PO…" on the transport planning
 *              boards (TransportPlanning and HaulageJobsPlanning), for the
 *              row's right-click menu and its three-dots menu alike.
 *
 *              Nothing about archiving is decided here. The order runs
 *              ArchiveOrderFlow and the PO runs ArchivePurchaseOrdersFlow —
 *              exactly what Search Customer Orders and Search Purchase Orders
 *              run: the same checklist, linked PO-Ds and PO-H, cancellation
 *              emails for sent POs before anything is archived, the same routes
 *              and audit. The board adds only:
 *
 *              - One row at a time. With more than one row selected, the
 *                action explains that and points at the search screens, which
 *                archive in bulk.
 *              - Who may: whoever may on the search screen — access to Sales
 *                Orders (order) or Purchase Orders (PO). Archiving is not a
 *                board edit, so it does not need Edit Transport Planning, and
 *                a locked or past day refuses it no more than the search
 *                screens do. View-only accounts get both greyed by the menus
 *                themselves (utils/readOnlyActions), and the server refuses.
 *              - Disabled with the reason, never hidden: a haulage job (not a
 *                customer order, no PO) and a stock line (no PO).
 *              - A board `source`, which files the archive in the day's Plan
 *                History as a plan change (utils/archiveSource.js).
 *              - A PO's verdict (billed, synced from Xero…) comes from the
 *                order's archive preview — poArchiveService.refusal, the rule
 *                the search screen shows — and is told before any dialog.
 *              - "Archive PO…" only when the row's order has two or more live
 *                PO-Ds (Cam, 3 Nov 2026 — SO-D10696: its one PO-D was archived
 *                from the board and the order was left behind with nothing
 *                bought for it). With one, the menu offers "Archive order…"
 *                alone. The count is the server's (`order_po_d_count`,
 *                poArchiveService.stampOrderPoDCounts); a row without it gets
 *                "Archive order…" alone too.
 *
 *              Usage:
 *                const archive = useBoardArchive({ user, selectedCount, isSnapshot,
 *                  source: 'Transport planning', onArchived: fetchOrders, showAlert });
 *                rowActions: [...archive.actionsFor(row)]
 *                render: {archive.element}
 */

import React, { useRef, useState } from 'react';
import ArchiveOrderFlow, { fetchOrderArchivePreview } from '../components/ArchiveOrderFlow';
import ArchivePurchaseOrdersFlow from '../components/ArchivePurchaseOrdersFlow';
import { hasPermission } from '../utils/permissions';
import { REDELIVERY_ARCHIVE_REASON } from '../utils/redelivery';
import { ordersOutcomeText } from '../utils/archiveOrdersLeft';

/** The row's local PO id, or null — stock rows carry a synthetic string key. */
export function boardRowPoId(row) {
  if (!row || row.is_haulage_job || row.is_stock_line) return null;
  const n = Number(row.purchase_order_id);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Why the row's order cannot be archived from the board, or null. */
export function orderArchiveBlock(row, user) {
  if (row?.is_haulage_job) return 'a haulage job, not a customer order — archive it on Haulage Jobs';
  if (row?.is_self_billed) return 'a self-billed PO has no customer order — archive the PO on Search Purchase Orders';
  // A redelivery's row (utils/redelivery): cancel the redelivery instead.
  if (row?.is_redelivery) return REDELIVERY_ARCHIVE_REASON;
  if (!row?.id) return 'there is no customer order behind this row';
  if (!hasPermission(user, 'nav.search-customer-orders')) return 'you do not have access to Sales Orders';
  return null;
}

/**
 * Whether the row's menu offers "Archive PO…" at all: only when its order has
 * two or more live PO-Ds, so archiving one still leaves the order bought for
 * (Cam, 3 Nov 2026). Haulage jobs, self-billed rows, redeliveries and stock
 * lines have no PO of the order's to archive here.
 */
export function offersPoArchive(row) {
  if (!row || row.is_haulage_job || row.is_self_billed || row.is_redelivery) return false;
  if (!boardRowPoId(row)) return false;
  const n = Number(row.order_po_d_count);
  return Number.isFinite(n) && n >= 2;
}

/** Why the row's PO cannot be archived from the board, or null. */
export function poArchiveBlock(row, user) {
  if (row?.is_haulage_job) return 'a haulage job has no purchase order';
  if (row?.is_self_billed) return 'a self-billed PO — archive it on Search Purchase Orders';
  if (row?.is_redelivery) return REDELIVERY_ARCHIVE_REASON;
  if (!boardRowPoId(row)) return 'supplied from stock — there is no purchase order';
  if (!row.id) return 'there is no customer order behind this row';
  if (!hasPermission(user, 'nav.search-purchase-orders')) return 'you do not have access to Purchase Orders';
  return null;
}

export default function useBoardArchive({ user, selectedCount = 0, isSnapshot = false, source, onArchived, showAlert }) {
  const [orderFor, setOrderFor] = useState(null);   // { id, order_number }
  const [posFor, setPosFor] = useState(null);       // [ArchivePOsFlow row]
  // A PO's archive preview is being read: a second click (or the other menu)
  // must not open a second checklist on top of the first.
  const loadingPo = useRef(false);
  const busy = () => loadingPo.current || !!orderFor || !!posFor;

  // More than one row selected: one at a time here, bulk on the search screens.
  const refuseBulk = (what) => {
    if (selectedCount <= 1) return false;
    showAlert({
      title: 'One row at a time',
      type: 'warning',
      message: `${selectedCount} rows are selected. Archiving from the board is one row at a time — right-click a single row, or, to archive several, use `
        + (what === 'order' ? 'Search Customer Orders.' : 'Search Purchase Orders.')
    });
    return true;
  };

  // A past day is served as it was planned (the day's snapshot), so its row stays.
  const pastNote = isSnapshot ? ' This is a past day, shown as it was planned, so the row stays on it.' : '';

  const finish = ({ type, message }) => {
    setOrderFor(null);
    setPosFor(null);
    showAlert({ title: type === 'error' ? 'Could not archive' : 'Archived', type, message: type === 'error' ? message : `${message}${pastNote}` });
    if (type !== 'error') onArchived();
  };

  const startPo = async (row) => {
    const poId = boardRowPoId(row);
    loadingPo.current = true;
    try {
      const preview = await fetchOrderArchivePreview(row.id);
      const po = (preview.pos || []).find(p => Number(p.id) === poId);
      if (!po) {
        // Archived or moved elsewhere since the board was read.
        showAlert({
          title: 'Not archived',
          type: 'warning',
          message: `${row.purchase_order_number || 'This purchase order'} is no longer a live PO on ${row.order_number || 'this order'} — the board has been refreshed.`
        });
        onArchived();
        return;
      }
      if (po.blocked) {
        showAlert({ title: 'Cannot archive', type: 'warning', message: `Cannot archive ${po.purchase_order_number} — ${po.blocked}.` });
        return;
      }
      setPosFor([po]);
    } catch (error) {
      showAlert({ title: 'Could not archive', type: 'error', message: error.message });
    } finally {
      loadingPo.current = false;
    }
  };

  /**
   * The menu actions for a row — the same in both menus: "Archive order…",
   * and "Archive PO…" only when the order has two or more PO-Ds.
   */
  const actionsFor = (row) => {
    const orderBlock = orderArchiveBlock(row, user);
    const poBlock = poArchiveBlock(row, user);
    const actions = [
      {
        key: 'archive-order',
        icon: 'archive',
        label: 'Archive order…',
        disabled: !!orderBlock,
        title: orderBlock
          ? `Cannot archive — ${orderBlock}`
          : `Archive ${row.order_number || 'the customer order'} — its POs can go with it, and a supplier or haulier who has one can be sent a cancellation first`,
        onClick: () => {
          if (orderBlock || busy() || refuseBulk('order')) return;
          setOrderFor({ id: row.id, order_number: row.order_number });
        }
      },
    ];
    if (offersPoArchive(row)) {
      actions.push({
        key: 'archive-po',
        icon: 'archive',
        label: 'Archive PO…',
        disabled: !!poBlock,
        title: poBlock
          ? `Cannot archive — ${poBlock}`
          : `Archive ${row.purchase_order_number || 'the purchase order'} only — ${row.order_number || 'the order'} keeps its other PO-Ds; offers the supplier a cancellation if they already have it`,
        onClick: () => {
          if (poBlock || busy() || refuseBulk('po')) return;
          startPo(row);
        }
      });
    }
    return actions;
  };

  const element = (
    <>
      {orderFor && (
        <ArchiveOrderFlow
          key={orderFor.id}
          order={orderFor}
          user={user}
          source={source}
          onDone={finish}
          onClose={() => setOrderFor(null)}
        />
      )}
      {posFor && (
        <ArchivePurchaseOrdersFlow
          pos={posFor}
          user={user}
          source={source}
          onDone={(data) => finish({
            type: (data.orders_kept || []).length ? 'warning' : 'success',
            message: `Archived ${(data.archived || []).map(p => p.purchase_order_number).join(', ')}. It is off its customer order and the planning boards — Search Purchase Orders can restore it.`
              + (ordersOutcomeText(data) ? ` ${ordersOutcomeText(data)}` : '')
          })}
          onClose={() => setPosFor(null)}
        />
      )}
    </>
  );

  return { actionsFor, element };
}
